import { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { sectorData, entityList, peerData } from '@/data/cctsCheckerData';

/* ── helpers ─────────────────────────────────── */
function formatINR(n) {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 10000000) return sign + 'Rs. ' + (abs / 10000000).toFixed(2).replace(/\.00$/, '') + ' crore';
  if (abs >= 100000) return sign + 'Rs. ' + (abs / 100000).toFixed(2).replace(/\.00$/, '') + ' lakh';
  return sign + 'Rs. ' + abs.toLocaleString('en-IN');
}

function percentileOf(value, sortedArr) {
  if (!sortedArr.length) return 0;
  const below = sortedArr.filter(v => v < value).length;
  return Math.round((below / sortedArr.length) * 100);
}

function requiredAnnualRate(baseVal, targetVal, years) {
  if (!baseVal || !targetVal || !years) return 0;
  const rate = 1 - Math.pow(targetVal / baseVal, 1 / years);
  return Math.round(rate * 1000) / 10;
}

function commaNum(n) { return Math.round(n).toLocaleString('en-IN'); }

/* ── chart components ────────────────────────── */
function PeerDistributionChart({ values, userValue, label, color = '#B8860B', unit = '' }) {
  if (!values.length) return null;
  const buckets = 10;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = range / buckets;
  const bins = Array(buckets).fill(0);
  values.forEach(v => { const i = Math.min(buckets - 1, Math.floor((v - min) / step)); bins[i]++; });
  const maxBin = Math.max(...bins);
  const userBin = Math.min(buckets - 1, Math.floor((userValue - min) / step));
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#7A7568', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{label}</p>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 60 }}>
        {bins.map((count, i) => (
          <motion.div key={i} initial={{ height: 0 }} animate={{ height: maxBin ? `${(count / maxBin) * 100}%` : 0 }}
            transition={{ delay: i * 0.03, duration: 0.3 }}
            style={{ flex: 1, minHeight: 2, borderRadius: '3px 3px 0 0', background: i === userBin ? color : '#DEDACD', position: 'relative' }}>
            {i === userBin && <div style={{ position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50)', fontSize: 9, color, fontWeight: 700, whiteSpace: 'nowrap' }}>You</div>}
          </motion.div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#7A7568', fontFamily: 'IBM Plex Mono, monospace', marginTop: 3 }}>
        <span>{commaNum(min)}{unit}</span><span>{commaNum(max)}{unit}</span>
      </div>
    </div>
  );
}

function ReductionPathwayChart({ baseline, tgt25, tgt26, userIntensity, color = '#B8860B' }) {
  if (!baseline) return null;
  const points = [
    { year: 'FY23-24', val: baseline },
    ...(tgt25 ? [{ year: 'CY25-26', val: tgt25 }] : []),
    ...(tgt26 ? [{ year: 'CY26-27', val: tgt26 }] : []),
  ];
  const allVals = [...points.map(p => p.val), userIntensity].filter(Boolean);
  const maxV = Math.max(...allVals) * 1.15;
  const minV = Math.min(...allVals) * 0.85;
  const range = maxV - minV || 1;
  const h = 100, w = 280;
  const toY = v => h - ((v - minV) / range) * h;
  const toX = (i) => 30 + (i / (points.length - 1)) * (w - 60);
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(i)},${toY(p.val)}`).join(' ');
  const userY = userIntensity ? toY(userIntensity) : null;
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#7A7568', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Reduction pathway</p>
      <svg viewBox={`0 0 ${w} ${h + 30}`} style={{ width: '100%', maxWidth: 360, height: 'auto' }}>
        {/* Grid lines */}
        {[0.25, 0.5, 0.75].map(f => <line key={f} x1={20} x2={w - 10} y1={h * f} y2={h * f} stroke="#DEDACD" strokeWidth="0.5" strokeDasharray="4 3" />)}
        {/* Target path */}
        <path d={pathD} fill="none" stroke="#3F6B4C" strokeWidth="2" strokeLinecap="round" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={toX(i)} cy={toY(p.val)} r="4" fill="#3F6B4C" stroke="#fff" strokeWidth="1.5" />
            <text x={toX(i)} y={toY(p.val) - 8} textAnchor="middle" fontSize="8" fill="#3F6B4C" fontWeight="700">{p.val.toFixed(3)}</text>
            <text x={toX(i)} y={h + 14} textAnchor="middle" fontSize="7" fill="#7A7568">{p.year}</text>
          </g>
        ))}
        {/* User intensity line */}
        {userY !== null && (
          <>
            <line x1={20} x2={w - 10} y1={userY} y2={userY} stroke={color} strokeWidth="1.5" strokeDasharray="5 3" />
            <text x={w - 8} y={userY - 4} textAnchor="end" fontSize="8" fill={color} fontWeight="700">You: {userIntensity.toFixed(3)}</text>
          </>
        )}
      </svg>
    </div>
  );
}

function SensitivityBarChart({ gapVal, prices, isSurplus, currentPrice }) {
  const vals = prices.map(p => isSurplus ? gapVal * p : gapVal * p * 2);
  const maxVal = Math.max(...vals) || 1;
  const safeColor = '#3F6B4C';
  const dangerColor = '#B04A2A';
  const barColor = isSurplus ? safeColor : dangerColor;
  return (
    <div style={{ marginBottom: 24 }}>
      <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#7A7568', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Price sensitivity</p>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, height: 120 }}>
        {prices.map((p, i) => {
          const isCurrent = p === currentPrice;
          const pct = (vals[i] / maxVal) * 100;
          return (
            <div key={p} style={{ flex: 1, textAlign: 'center' }}>
              <motion.div initial={{ height: 0 }} animate={{ height: `${pct}%` }} transition={{ delay: i * 0.1, duration: 0.4 }}
                style={{ background: isCurrent ? barColor : barColor + '50', borderRadius: '6px 6px 0 0', marginBottom: 6, position: 'relative', minHeight: 4 }}>
                <span style={{ position: 'absolute', top: -20, left: '50%', transform: 'translateX(-50%)', fontSize: 11, fontWeight: 700, color: barColor, whiteSpace: 'nowrap' }}>{formatINR(vals[i])}</span>
              </motion.div>
              <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: isCurrent ? '#B8860B' : '#7A7568' }}>Rs. {p.toLocaleString('en-IN')}</p>
              {isCurrent && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#B8860B', margin: '4px auto 0' }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GapDonutChart({ gapVal, totalEmissions, isSurplus }) {
  if (!totalEmissions) return null;
  const pct = Math.min(100, Math.round((gapVal / totalEmissions) * 100));
  const radius = 50, stroke = 10;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (pct / 100) * circ;
  const color = isSurplus ? '#3F6B4C' : '#B04A2A';
  return (
    <div style={{ textAlign: 'center', marginBottom: 16 }}>
      <svg width="130" height="130" viewBox="0 0 130 130">
        <circle cx="65" cy="65" r={radius} fill="none" stroke="#F0EEE6" strokeWidth={stroke} />
        <motion.circle cx="65" cy="65" r={radius} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} initial={{ strokeDashoffset: circ }} animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          strokeLinecap="round" transform="rotate(-90 65 65)" />
        <text x="65" y="60" textAnchor="middle" fontSize="22" fontWeight="700" fill={color} fontFamily="Space Grotesk, sans-serif">{pct}%</text>
        <text x="65" y="76" textAnchor="middle" fontSize="9" fill="#7A7568">{isSurplus ? 'surplus' : 'gap'}</text>
      </svg>
      <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#7A7568', marginTop: 4 }}>{commaNum(gapVal)} of {commaNum(totalEmissions)} tCO₂e baseline</p>
    </div>
  );
}

function SectorComparisonChart({ sectorData: sd, sector, userIntensity }) {
  if (!sd || !sector) return null;
  const subs = Object.entries(sd[sector]?.subsectors || {});
  if (!subs.length) return null;
  const maxBase = Math.max(...subs.map(([, v]) => v.baseMed));
  return (
    <div style={{ marginTop: 16 }}>
      <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#7A7568', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Sub-sector baseline comparison</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {subs.map(([name, v]) => {
          const pct = maxBase ? (v.baseMed / maxBase) * 100 : 0;
          return (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 120, fontSize: 11, color: '#242018', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
              <div style={{ flex: 1, height: 16, background: '#F0EEE6', borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
                <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.5 }}
                  style={{ height: '100%', background: '#B8860B40', borderRadius: 4 }} />
                {userIntensity > 0 && (
                  <div style={{ position: 'absolute', left: `${Math.min(98, (userIntensity / maxBase) * 100)}%`, top: 0, bottom: 0, width: 2, background: '#B04A2A' }} />
                )}
              </div>
              <span style={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', color: '#7A7568', width: 50, flexShrink: 0 }}>{v.baseMed.toFixed(3)}</span>
            </div>
          );
        })}
      </div>
      {userIntensity > 0 && <p style={{ fontSize: 9, color: '#B04A2A', marginTop: 6 }}>Red line = your intensity ({userIntensity.toFixed(4)})</p>}
    </div>
  );
}

const SECTORS = ['Aluminium', 'Cement', 'Chlor-Alkali', 'Pulp & Paper', 'Petrochemical', 'Petroleum Refining', 'Textile'];
const DISABLED_SECTORS = [{ name: 'Iron & Steel', tag: 'Pending notification' }, { name: 'Fertilizer', tag: 'Pending notification' }];

/* ── component ───────────────────────────────── */
const CCTSCheckerPage = () => {
  const [step, setStep] = useState(1);

  // Step 1 state
  const [search, setSearch] = useState('');
  const [matched, setMatched] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [sector, setSector] = useState('');
  const [subSector, setSubSector] = useState('');
  const [production, setProduction] = useState('');
  const [err1, setErr1] = useState('');

  // Step 2 state
  const [knowsIntensity, setKnowsIntensity] = useState(null);
  const [intensity, setIntensity] = useState('');
  const [selectedYear, setSelectedYear] = useState('2526');
  const [isEstimate, setIsEstimate] = useState(false);
  const [err2, setErr2] = useState('');

  // Step 3 state
  const [price, setPrice] = useState(1200);

  // Results state
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [emailErr, setEmailErr] = useState('');

  /* ── derived data ──────────────────────────── */
  const d = useMemo(() => {
    const s = sectorData[sector];
    if (!sector || !subSector || !s) return null;
    return s.subsectors[subSector] || null;
  }, [sector, subSector]);

  const peers = useMemo(() => {
    return peerData[`${sector}|${subSector}`] || { prods: [], bases: [] };
  }, [sector, subSector]);

  const prod = parseFloat(production) || 0;
  const intVal = parseFloat(intensity) || 0;

  const statusBadge = useMemo(() => {
    if (!d || !prod) return null;
    if (prod >= d.prodMin && prod <= d.prodMax) return { text: 'Within the notified range', color: 'safe' };
    if (prod >= d.prodMin * 0.7 && prod < d.prodMin) return { text: 'Close to the smallest notified unit', color: 'borderline' };
    return { text: 'Outside the currently notified range', color: 'warn' };
  }, [d, prod]);

  const prodPctile = useMemo(() => percentileOf(prod, peers.prods), [prod, peers]);

  const targetVal = useMemo(() => {
    if (!d) return 0;
    return selectedYear === '2526' ? (d.tgt25 || 0) : (d.tgt26 || 0);
  }, [d, selectedYear]);

  const gapVal = useMemo(() => {
    if (!intVal || !targetVal || !prod) return 0;
    return Math.round(Math.abs(intVal - targetVal) * prod);
  }, [intVal, targetVal, prod]);

  const isSurplus = intVal < targetVal;
  const totalEmissions = useMemo(() => intVal > 0 && prod > 0 ? Math.round(intVal * prod) : 0, [intVal, prod]);

  const mandatedRate = useMemo(() => {
    if (!d) return 0;
    return requiredAnnualRate(d.baseMed, selectedYear === '2526' ? d.tgt25 : d.tgt26, selectedYear === '2526' ? 2 : 3);
  }, [d, selectedYear]);

  const pctAboveTarget = targetVal > 0 ? Math.round(((intVal - targetVal) / targetVal) * 1000) / 10 : 0;
  const intPctile = useMemo(() => percentileOf(intVal, peers.bases), [intVal, peers]);

  /* ── autocomplete ──────────────────────────── */
  const suggestions = useMemo(() => {
    if (search.length < 2) return [];
    const q = search.toLowerCase();
    return entityList.filter(e => e.name.toLowerCase().includes(q)).slice(0, 8);
  }, [search]);

  const selectEntity = useCallback((e) => {
    setMatched(e);
    setSearch(e.name);
    setSector(e.sector);
    const subs = Object.keys(sectorData[e.sector]?.subsectors || {});
    setSubSector(e.sub || subs[0] || '');
    setProduction(String(e.prod));
    setNotFound(false);
    setManualMode(false);
  }, [setMatched, setSearch, setSector, setSubSector, setProduction, setNotFound, setManualMode]);

  const handleSearchChange = (val) => {
    setSearch(val);
    setMatched(null);
    setNotFound(false);
    if (val.length >= 4) {
      const q = val.toLowerCase();
      const exact = entityList.find(e => e.name.toLowerCase() === q);
      if (exact) { selectEntity(exact); return; }
      const any = entityList.some(e => e.name.toLowerCase().includes(q));
      if (!any) setNotFound(true);
    }
  };

  const goManual = () => {
    setManualMode(true);
    setMatched(null);
    setSearch('');
    setSector('');
    setSubSector('');
    setProduction('');
  };

  const pickSector = (s) => {
    setSector(s);
    const subs = Object.keys(sectorData[s]?.subsectors || {});
    setSubSector(subs.length === 1 ? subs[0] : '');
    if (!matched) setProduction('');
  };

  /* ── navigation ────────────────────────────── */
  const goStep = (n) => { setStep(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const next1 = () => {
    if (!sector || !subSector) { setErr1('Select a sector and sub-sector.'); return; }
    if (!prod || prod <= 0) { setErr1('Enter your annual production.'); return; }
    setErr1(''); goStep(2);
  };

  const next2 = () => {
    if (knowsIntensity === null) { setErr2('Tell us if you know your intensity.'); return; }
    if (knowsIntensity && (!intVal || intVal <= 0)) { setErr2('Enter your emissions intensity.'); return; }
    setErr2(''); goStep(3);
  };

  const handleKnowsIntensity = (knows) => {
    setKnowsIntensity(knows);
    if (!knows && d) {
      setIntensity(String(d.baseMed));
      setIsEstimate(true);
    } else {
      setIsEstimate(false);
      if (!knows) setIntensity('');
    }
  };

  const doUnlock = () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setEmailErr('Enter a valid email address.'); return; }
    if (!role) { setEmailErr('Select your role.'); return; }
    setEmailErr('');
    setUnlocked(true);
  };

  /* ── shared styles ─────────────────────────── */
  const colors = { bg: '#F7F6F1', surface: '#FFFFFF', surface2: '#F0EEE6', border: '#DEDACD', text: '#242018', muted: '#7A7568', warn: '#B8860B', obligated: '#B04A2A', safe: '#3F6B4C', borderline: '#96731F' };
  const badgeColors = { safe: colors.safe, borderline: colors.borderline, warn: colors.obligated };

  /* ── deadline logic ────────────────────────── */
  const deadlineDate = new Date('2026-07-31T23:59:59+05:30');
  const now = new Date();
  const isPast = now > deadlineDate;
  const daysAway = isPast ? 0 : Math.ceil((deadlineDate - now) / 86400000);

  /* ── sector aggregate ──────────────────────── */
  const sectorAgg = useMemo(() => {
    if (!d || !targetVal) return 0;
    const totalProd = peers.prods.reduce((a, b) => a + b, 0);
    const gapPerUnit = Math.abs(d.baseMed - targetVal);
    return Math.round(totalProd * gapPerUnit * price * 2);
  }, [d, targetVal, price, peers.prods]);

  /* ── sensitivity prices ────────────────────── */
  const sensPrices = [500, 1200, 2500];

  /* ── progress bar ──────────────────────────── */
  const progress = (
    <div style={{ display: 'flex', gap: 6, marginBottom: 32 }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{ flex: 1, height: 4, background: colors.border, borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: step >= i ? '100%' : '0%', height: '100%', background: colors.warn, transition: 'width .4s ease', borderRadius: 2 }} />
        </div>
      ))}
    </div>
  );

  /* ── sub-sector options ────────────────────── */
  const subOptions = sector ? Object.keys(sectorData[sector]?.subsectors || {}) : [];

  return (
    <main style={{ background: colors.bg, minHeight: '100vh', fontFamily: 'Inter, sans-serif', color: colors.text }} data-testid="ccts-checker-page">
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '120px 24px 80px' }}>
        {/* Header */}
        <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 13, letterSpacing: '0.12em', color: colors.muted, textTransform: 'uppercase', marginBottom: 8 }}>Maple Integrated Solutions</p>
        <h1 style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 36, fontWeight: 700, color: colors.text, margin: '0 0 8px', lineHeight: 1.15 }}>CCTS Readiness Checker</h1>
        <p style={{ fontSize: 14, color: colors.muted, marginBottom: 32 }}>Benchmarked against entities already notified under the Carbon Credit Trading Scheme, 2023</p>

        {progress}

        {/* ═══ STEP 1 ═══ */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 32 }}>
            <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: colors.warn, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Step 1 of 3</p>
            <h2 style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 24, fontWeight: 700, margin: '8px 0 24px' }}>Where do I sit?</h2>

            {/* Company search */}
            {!manualMode && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, color: colors.muted, display: 'block', marginBottom: 6 }}>Your company or unit name</label>
                <input
                  data-testid="company-search"
                  value={search}
                  onChange={e => handleSearchChange(e.target.value)}
                  placeholder="Start typing to search notified entities..."
                  style={{ width: '100%', padding: '10px 14px', border: `1px solid ${colors.border}`, borderRadius: 8, background: colors.surface2, fontSize: 15, outline: 'none', boxSizing: 'border-box' }}
                />
                {suggestions.length > 0 && !matched && (
                  <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, marginTop: 4, maxHeight: 200, overflow: 'auto', background: colors.surface }}>
                    {suggestions.map((e, i) => (
                      <div key={`sugg-${e.name}`} onClick={() => selectEntity(e)} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${colors.border}`, fontSize: 13 }}
                        onMouseEnter={ev => ev.currentTarget.style.background = colors.surface2}
                        onMouseLeave={ev => ev.currentTarget.style.background = colors.surface}>
                        <strong>{e.name}</strong>
                        <span style={{ color: colors.muted, marginLeft: 8 }}>{e.sector} &middot; {e.sub}</span>
                      </div>
                    ))}
                  </div>
                )}
                {matched && (
                  <div data-testid="matched-note" style={{ marginTop: 12, padding: 14, background: '#f0f7f1', border: `1px solid ${colors.safe}40`, borderRadius: 8, fontSize: 13, color: colors.safe }}>
                    <strong>Matched:</strong> {matched.sector} &middot; {matched.sub} &middot; Baseline production: {commaNum(matched.prod)} tonnes &middot; Baseline intensity: {matched.base} {sectorData[matched.sector]?.unit}
                  </div>
                )}
                {notFound && (
                  <div data-testid="notfound-note" style={{ marginTop: 12, padding: 14, background: '#fdf6ee', border: `1px solid ${colors.warn}40`, borderRadius: 8, fontSize: 13 }}>
                    You&apos;re not currently on the CCTS notified entity list under that name. That doesn&apos;t mean you&apos;re exempt&mdash;more sectors are being phased in and thresholds shift. Want to see where you&apos;d likely land anyway?
                    <button onClick={goManual} style={{ display: 'block', marginTop: 8, background: colors.warn, color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Continue anyway</button>
                  </div>
                )}
                <button onClick={goManual} style={{ background: 'none', border: 'none', color: colors.muted, fontSize: 13, cursor: 'pointer', marginTop: 12, textDecoration: 'underline' }}>Prefer to pick your sector directly?</button>
              </div>
            )}

            {/* Sector grid */}
            {(manualMode || matched) && (
              <>
                <label style={{ fontSize: 13, color: colors.muted, display: 'block', marginBottom: 8 }}>Sector</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
                  {SECTORS.map(s => (
                    <button key={s} data-testid={`sector-btn-${s}`} onClick={() => pickSector(s)}
                      style={{ padding: '14px 10px', border: `1px solid ${sector === s ? colors.warn : colors.border}`, borderRadius: 8, background: sector === s ? colors.warn + '15' : colors.surface, cursor: 'pointer', fontSize: 13, fontWeight: sector === s ? 700 : 400, textAlign: 'center', color: colors.text }}>
                      {s}
                    </button>
                  ))}
                  {DISABLED_SECTORS.map(s => (
                    <button key={s.name} disabled style={{ padding: '14px 10px', border: `1px solid ${colors.border}`, borderRadius: 8, background: colors.surface2, cursor: 'not-allowed', fontSize: 13, color: colors.muted, textAlign: 'center', opacity: 0.5 }}>
                      {s.name}<br /><span style={{ fontSize: 10 }}>{s.tag}</span>
                    </button>
                  ))}
                </div>

                {/* Sub-sector */}
                {sector && subOptions.length > 1 && (
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 13, color: colors.muted, display: 'block', marginBottom: 6 }}>Sub-sector</label>
                    <select data-testid="subsector-select" value={subSector} onChange={e => setSubSector(e.target.value)}
                      style={{ width: '100%', padding: '10px 14px', border: `1px solid ${colors.border}`, borderRadius: 8, background: colors.surface2, fontSize: 14 }}>
                      <option value="">Select...</option>
                      {subOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}

                {/* Production */}
                {subSector && (
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 13, color: colors.muted, display: 'block', marginBottom: 6 }}>Annual production ({sector === 'Petroleum Refining' ? 'MBBLS' : 'tonnes'}/year)</label>
                    <input data-testid="production-input" type="number" value={production} onChange={e => setProduction(e.target.value)}
                      style={{ width: '100%', padding: '10px 14px', border: `1px solid ${colors.border}`, borderRadius: 8, background: colors.surface2, fontSize: 15, boxSizing: 'border-box' }} />

                    {/* Status badge */}
                    {statusBadge && prod > 0 && (
                      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ background: badgeColors[statusBadge.color] + '20', color: badgeColors[statusBadge.color], padding: '4px 10px', borderRadius: 20, fontSize: 12, fontFamily: 'IBM Plex Mono, monospace' }}>{statusBadge.text}</span>
                      </div>
                    )}

                    {/* Percentile + range bar */}
                    {d && prod > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 32, fontWeight: 700, color: colors.warn, margin: 0 }}>{prodPctile}<sup style={{ fontSize: 14 }}>{prodPctile === 1 ? 'st' : prodPctile === 2 ? 'nd' : prodPctile === 3 ? 'rd' : 'th'}</sup> <span style={{ fontSize: 14, fontWeight: 400, color: colors.muted }}>percentile among {d.n} notified peers</span></p>
                        <div style={{ position: 'relative', height: 8, background: colors.surface2, borderRadius: 4, marginTop: 10 }}>
                          <div style={{ position: 'absolute', left: `${Math.min(98, Math.max(2, ((d.prodMed - d.prodMin) / (d.prodMax - d.prodMin)) * 100))}%`, top: -4, width: 1, height: 16, background: colors.muted }} />
                          <div style={{ position: 'absolute', left: `${Math.min(98, Math.max(2, ((prod - d.prodMin) / (d.prodMax - d.prodMin)) * 100))}%`, top: -3, width: 10, height: 10, borderRadius: '50%', background: colors.warn, border: '2px solid #fff', transform: 'translateX(-5px)' }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: colors.muted, fontFamily: 'IBM Plex Mono, monospace', marginTop: 4 }}>
                          <span>{commaNum(d.prodMin)}</span>
                          <span>median {commaNum(d.prodMed)}</span>
                          <span>{commaNum(d.prodMax)}</span>
                        </div>
                      </div>
                    )}

                    {/* Peer production distribution chart */}
                    {peers.prods.length > 3 && <PeerDistributionChart values={peers.prods} userValue={prod} label="Production distribution among peers" unit="" />}
                  </div>
                )}
              </>
            )}

            {err1 && <p style={{ color: colors.obligated, fontSize: 13, margin: '12px 0' }}>{err1}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
              <button data-testid="next-step1" onClick={next1} style={{ background: colors.text, color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Next</button>
            </div>
          </motion.div>
        )}

        {/* ═══ STEP 2 ═══ */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 32 }}>
            <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: colors.warn, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Step 2 of 3</p>
            <h2 style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 24, fontWeight: 700, margin: '8px 0 24px' }}>How does your intensity compare?</h2>

            {/* Knows intensity toggle */}
            <p style={{ fontSize: 14, marginBottom: 12 }}>Do you have a verified emissions intensity number?</p>
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              {[true, false].map(v => (
                <button key={String(v)} onClick={() => handleKnowsIntensity(v)} data-testid={v ? 'knows-yes' : 'knows-no'}
                  style={{ flex: 1, padding: '12px', border: `1px solid ${knowsIntensity === v ? colors.warn : colors.border}`, borderRadius: 8, background: knowsIntensity === v ? colors.warn + '15' : colors.surface, cursor: 'pointer', fontSize: 13, fontWeight: knowsIntensity === v ? 700 : 400 }}>
                  {v ? 'Yes, I know it' : 'Not sure / I don\'t have one'}
                </button>
              ))}
            </div>

            {/* Intensity input */}
            {knowsIntensity === true && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, color: colors.muted, display: 'block', marginBottom: 6 }}>Current emissions intensity ({d?.unit || sectorData[sector]?.unit || 'tCO2e/t'})</label>
                <input data-testid="intensity-input" type="number" step="0.001" value={intensity} onChange={e => setIntensity(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', border: `1px solid ${colors.border}`, borderRadius: 8, background: colors.surface2, fontSize: 15, boxSizing: 'border-box' }} />
              </div>
            )}

            {/* Not sure block */}
            {knowsIntensity === false && (
              <div style={{ padding: 16, background: colors.surface2, borderRadius: 8, marginBottom: 20, fontSize: 13, color: colors.muted, lineHeight: 1.6 }}>
                That&apos;s the normal answer, not the exception. Most obligated entities don&apos;t have a verified baseline yet&mdash;establishing one is exactly what standing up CCTS compliance involves. We&apos;ll show you where you&apos;d likely sit using your sub-sector&apos;s median, but treat this as a placeholder, not your real number.
              </div>
            )}

            {/* Year selector */}
            {knowsIntensity !== null && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, color: colors.muted, display: 'block', marginBottom: 6 }}>Compliance year</label>
                <select data-testid="year-select" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}
                  style={{ padding: '10px 14px', border: `1px solid ${colors.border}`, borderRadius: 8, background: colors.surface2, fontSize: 14 }}>
                  {d?.tgt25 && <option value="2526">CY 2025-26</option>}
                  {d?.tgt26 && <option value="2627">CY 2026-27</option>}
                </select>
              </div>
            )}

            {/* Trajectory row */}
            {knowsIntensity !== null && d && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
                {[
                  { label: 'FY2023-24 baseline', val: d.baseMed, active: false },
                  ...(d.tgt25 ? [{ label: 'CY2025-26 target', val: d.tgt25, active: selectedYear === '2526' }] : []),
                  ...(d.tgt26 ? [{ label: 'CY2026-27 target', val: d.tgt26, active: selectedYear === '2627' }] : []),
                ].map((t, i) => (
                  <div key={i} style={{ flex: 1, padding: 12, background: t.active ? colors.warn + '15' : colors.surface2, border: `1px solid ${t.active ? colors.warn : colors.border}`, borderRadius: 8, textAlign: 'center' }}>
                    <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: colors.muted, marginBottom: 4 }}>{t.label}</p>
                    <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 20, fontWeight: 700, color: t.active ? colors.warn : colors.text }}>{t.val?.toFixed(4)}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Gauge */}
            {intVal > 0 && targetVal > 0 && (
              <>
                <div style={{ position: 'relative', height: 12, background: colors.surface2, borderRadius: 6, marginBottom: 8, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, Math.max(4, (intVal / (targetVal * 1.6)) * 100))}%`, background: isSurplus ? colors.safe : colors.obligated, borderRadius: 6, transition: 'width .3s' }} />
                  <div style={{ position: 'absolute', left: `${(1 / 1.6) * 100}%`, top: 0, bottom: 0, width: 2, background: colors.text }} />
                </div>
                <div style={{ fontSize: 12, color: colors.muted, display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                  <span>Your intensity: {intVal.toFixed(4)}</span>
                  <span>Target: {targetVal.toFixed(4)}</span>
                </div>

                {/* Gap readout */}
                <div data-testid="gap-readout" style={{ padding: 16, background: isSurplus ? colors.safe + '10' : colors.obligated + '10', border: `1px solid ${isSurplus ? colors.safe : colors.obligated}30`, borderRadius: 8, marginBottom: 16, textAlign: 'center' }}>
                  <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 28, fontWeight: 700, color: isSurplus ? colors.safe : colors.obligated }}>{commaNum(gapVal)} tCO₂e</p>
                  <p style={{ fontSize: 13, color: colors.muted }}>{isSurplus ? 'Surplus' : 'Gap'} = |{intVal.toFixed(4)} − {targetVal.toFixed(4)}| × {commaNum(prod)}</p>
                </div>
                {isEstimate && <p style={{ fontSize: 12, color: colors.warn, fontStyle: 'italic', marginBottom: 16 }}>⚠ This uses your sub-sector&apos;s median intensity as a placeholder, not your actual verified number.</p>}

                {/* Three stat tiles */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
                  <div style={{ padding: 14, background: colors.surface2, borderRadius: 8, textAlign: 'center' }}>
                    <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 24, fontWeight: 700, color: colors.warn }}>{mandatedRate}%</p>
                    <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: colors.muted }}>Sector&apos;s mandated pace (per yr)</p>
                  </div>
                  <div style={{ padding: 14, background: colors.surface2, borderRadius: 8, textAlign: 'center' }}>
                    <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 24, fontWeight: 700, color: pctAboveTarget > 0 ? colors.obligated : colors.safe }}>{pctAboveTarget > 0 ? '+' : ''}{pctAboveTarget}%</p>
                    <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: colors.muted }}>You vs {selectedYear === '2526' ? 'CY25-26' : 'CY26-27'} target</p>
                  </div>
                  <div style={{ padding: 14, background: colors.surface2, borderRadius: 8, textAlign: 'center' }}>
                    <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 24, fontWeight: 700, color: colors.warn }}>{intPctile}<sup style={{ fontSize: 10 }}>th</sup></p>
                    <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: colors.muted }}>vs peer 2023-24 baselines</p>
                  </div>
                </div>

                {/* Intensity distribution + Reduction pathway charts */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {peers.bases.length > 3 && <PeerDistributionChart values={peers.bases} userValue={intVal} label="Intensity distribution among peers" color={isSurplus ? colors.safe : colors.obligated} />}
                  {d && <ReductionPathwayChart baseline={d.baseMed} tgt25={d.tgt25} tgt26={d.tgt26} userIntensity={intVal} color={isSurplus ? colors.safe : colors.obligated} />}
                </div>
              </>
            )}

            {err2 && <p style={{ color: colors.obligated, fontSize: 13, margin: '12px 0' }}>{err2}</p>}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <button onClick={() => goStep(1)} style={{ background: 'none', border: `1px solid ${colors.border}`, padding: '12px 24px', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Back</button>
              <button data-testid="next-step2" onClick={next2} style={{ background: colors.text, color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Next</button>
            </div>
          </motion.div>
        )}

        {/* ═══ STEP 3 ═══ */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 32 }}>
            <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: colors.warn, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Step 3 of 3</p>
            <h2 style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 24, fontWeight: 700, margin: '8px 0 24px' }}>What does it cost?</h2>

            <label style={{ fontSize: 13, color: colors.muted, display: 'block', marginBottom: 6 }}>Carbon price (Rs. per CCC)</label>
            <input data-testid="price-slider" type="range" min={500} max={2500} step={50} value={price} onChange={e => setPrice(Number(e.target.value))}
              style={{ width: '100%', marginBottom: 4 }} />
            <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 20, fontWeight: 700, color: colors.warn, textAlign: 'center', marginBottom: 24 }}>Rs. {price.toLocaleString('en-IN')} / CCC</p>

            {/* Hero financial number */}
            <div data-testid="penalty-box" style={{ padding: 24, background: isSurplus ? colors.safe + '10' : colors.obligated + '10', border: `1px solid ${isSurplus ? colors.safe : colors.obligated}30`, borderRadius: 10, textAlign: 'center', marginBottom: 24 }}>
              <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 36, fontWeight: 700, color: isSurplus ? colors.safe : colors.obligated }}>
                {isSurplus ? formatINR(gapVal * price) : formatINR(gapVal * price * 2)}
              </p>
              <p style={{ fontSize: 13, color: colors.muted, marginTop: 8 }}>
                {isSurplus
                  ? `Potential CCC sale value = ${commaNum(gapVal)} tCO₂e × Rs. ${price.toLocaleString('en-IN')}`
                  : `Penalty exposure (CPCB environmental compensation) = ${commaNum(gapVal)} tCO₂e × Rs. ${price.toLocaleString('en-IN')} × 2`}
              </p>
              <p style={{ fontSize: 11, color: colors.muted, marginTop: 6, fontStyle: 'italic' }}>
                {isSurplus
                  ? 'If verified, this surplus is issuable as CCCs and tradeable once trading opens on power exchanges.'
                  : 'Under Section 14 of the Energy Conservation (Amendment) Act, 2022, CPCB imposes environmental compensation at 2× the average CCC market price per tonne of shortfall.'}
              </p>
            </div>

            {/* Price sensitivity bar chart */}
            <SensitivityBarChart gapVal={gapVal} prices={sensPrices} isSurplus={isSurplus} currentPrice={price} />

            {/* Price sensitivity table (numbers) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 24 }}>
              {sensPrices.map(p => {
                const val = isSurplus ? gapVal * p : gapVal * p * 2;
                const isCurrent = p === price;
                return (
                  <div key={p} style={{ padding: 14, background: isCurrent ? colors.warn + '15' : colors.surface2, border: `1px solid ${isCurrent ? colors.warn : colors.border}`, borderRadius: 8, textAlign: 'center' }}>
                    <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: colors.muted }}>@ Rs. {p.toLocaleString('en-IN')}</p>
                    <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 18, fontWeight: 700, color: isSurplus ? colors.safe : colors.obligated, marginTop: 4 }}>{formatINR(val)}</p>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <button onClick={() => goStep(2)} style={{ background: 'none', border: `1px solid ${colors.border}`, padding: '12px 24px', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Back</button>
              <button data-testid="see-result" onClick={() => goStep(4)} style={{ background: colors.text, color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>See my full result</button>
            </div>
          </motion.div>
        )}

        {/* ═══ RESULTS ═══ */}
        {step === 4 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            {/* Deadline alert */}
            <div style={{ padding: 16, background: colors.obligated + '10', border: `1px solid ${colors.obligated}30`, borderRadius: 8, marginBottom: 24, fontSize: 13, lineHeight: 1.6 }}>
              {isPast ? (
                <><strong style={{ color: colors.obligated }}>The FY 2025-26 Form A deadline (31 July 2026) has passed.</strong> Entities that missed it can be deemed by BEE to have submitted at their unimproved FY2023-24 baseline, which maximizes the shortfall and penalty calculated above rather than reducing it. If Form A hasn&apos;t been filed yet, this is already an active enforcement risk under CPCB, not a future one.</>
              ) : (
                <>The FY 2025-26 Form A deadline is <strong>31 July 2026</strong>, <strong style={{ color: colors.obligated }}>{daysAway} days away</strong>. Verified GHG data must reach BEE through an accredited ACVA by then. Missing it lets BEE deem the entity compliant at its unimproved FY2023-24 baseline, maximizing the shortfall shown above.</>
              )}
            </div>

            {/* Closing copy */}
            <div style={{ marginBottom: 24, fontSize: 14, lineHeight: 1.7, color: colors.text }}>
              {knowsIntensity ? (
                <p>This took you sixty seconds. Your compliance officer will spend six months on the same question. The full model reads your baseline, your intensity trajectory, your abatement options and your verification risk, and tells you what to do about it. Let&apos;s run yours.</p>
              ) : (
                <p>You don&apos;t have a verified number yet, and that&apos;s the point: neither do most obligated entities. What you&apos;re looking at is a placeholder built from your sub-sector&apos;s median. Getting your actual baseline measured and verified is step one of CCTS compliance, and it&apos;s where we start.</p>
              )}
            </div>

            {/* Sector aggregate */}
            {sectorAgg > 0 && (
              <div style={{ padding: 16, background: colors.surface2, borderRadius: 8, marginBottom: 24, fontSize: 13, color: colors.muted, lineHeight: 1.6 }}>
                If all <strong style={{ color: colors.text }}>{d?.n}</strong> notified entities carried the sector&apos;s own baseline-to-target gap uncovered at Rs. {price.toLocaleString('en-IN')}/CCC, penalty exposure across the sub-sector would total <strong style={{ color: colors.obligated }}>{formatINR(sectorAgg)}</strong>, at the same 2× CPCB rate.
              </div>
            )}

            {/* Visual charts — Donut + Sector comparison */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <GapDonutChart gapVal={gapVal} totalEmissions={totalEmissions} isSurplus={isSurplus} />
              </div>
              <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 18 }}>
                <SectorComparisonChart sectorData={sectorData} sector={sector} userIntensity={intVal} />
              </div>
            </div>

            {/* Result cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 24 }}>
              {[
                { label: 'Sector & Status', value: `${sector} · ${subSector}`, sub: statusBadge?.text || '', locked: false },
                { label: isSurplus ? 'Potential CCC Revenue' : 'Penalty Exposure', value: isSurplus ? formatINR(gapVal * price) : formatINR(gapVal * price * 2), sub: `at Rs. ${price.toLocaleString('en-IN')}/CCC`, locked: false },
                { label: 'Sector Reduction Path', value: `${mandatedRate}% per year`, sub: 'Mandated annual cut', locked: true },
                { label: 'Percentile vs Peers', value: `${prodPctile}th by output`, sub: `Among ${d?.n || 0} notified entities`, locked: true },
                { label: 'Verification & Registry', value: 'ACVA + ICM Registry', sub: 'Status pending', locked: true },
              ].map((card, i) => (
                <div key={i} style={{
                  padding: 18, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10,
                  position: 'relative', overflow: 'hidden',
                  ...(card.locked && !unlocked ? { filter: 'blur(5px)', userSelect: 'none' } : {}),
                  ...(i === 4 ? { gridColumn: '1 / -1' } : {})
                }}>
                  <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{card.label}</p>
                  <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 20, fontWeight: 700, color: colors.text, margin: '6px 0 2px' }}>{card.value}</p>
                  <p style={{ fontSize: 12, color: colors.muted }}>{card.sub}</p>
                  {card.locked && !unlocked && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>🔒</div>}
                </div>
              ))}
            </div>

            {/* Email gate */}
            {!unlocked && (
              <div data-testid="email-gate" style={{ padding: 24, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10, marginBottom: 24 }}>
                <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Unlock your full result</p>
                <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                  <input data-testid="email-input" type="email" placeholder="Your work email" value={email} onChange={e => setEmail(e.target.value)}
                    style={{ flex: 1, minWidth: 200, padding: '10px 14px', border: `1px solid ${colors.border}`, borderRadius: 8, background: colors.surface2, fontSize: 14 }} />
                  <select data-testid="role-select" value={role} onChange={e => setRole(e.target.value)}
                    style={{ padding: '10px 14px', border: `1px solid ${colors.border}`, borderRadius: 8, background: colors.surface2, fontSize: 13 }}>
                    <option value="">Your role</option>
                    <option>Compliance officer</option>
                    <option>CFO or Finance</option>
                    <option>Sustainability or ESG lead</option>
                    <option>Founder or CEO</option>
                    <option>Other</option>
                  </select>
                </div>
                {emailErr && <p style={{ color: colors.obligated, fontSize: 12, marginBottom: 8 }}>{emailErr}</p>}
                <button data-testid="unlock-btn" onClick={doUnlock} style={{ background: colors.warn, color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, width: '100%' }}>Unlock my full result</button>
              </div>
            )}

            {unlocked && (
              <>
                <p style={{ fontSize: 13, color: colors.safe, marginBottom: 16 }}>✓ Full result unlocked. Here&apos;s your complete picture.</p>
                <a href="/contact" style={{ display: 'block', textAlign: 'center', background: colors.text, color: '#fff', padding: '14px 28px', borderRadius: 8, textDecoration: 'none', fontSize: 15, fontWeight: 600, marginBottom: 24 }}>Talk to Maple about your result</a>
              </>
            )}

            {/* Next steps */}
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Recommended next steps</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                {[
                  { n: 1, t: 'Baseline verification audit', d: 'Establish your verified FY2023-24 GHG emission intensity through an accredited ACVA.' },
                  { n: 2, t: 'Target trajectory mapping', d: 'Model your reduction pathway against CY2025-26 and CY2026-27 compliance targets.' },
                  { n: 3, t: 'Verification readiness review', d: 'Assess your MRV systems, data quality, and reporting against BEE Form A requirements.' },
                  { n: 4, t: 'Compliance risk consultation', d: 'Evaluate penalty exposure, CCC trading strategy, and banking options under CCTS.' },
                ].map(s => (
                  <div key={s.n} style={{ padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8 }}>
                    <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 24, fontWeight: 700, color: colors.warn }}>{s.n}</span>
                    <p style={{ fontSize: 14, fontWeight: 600, margin: '6px 0 4px' }}>{s.t}</p>
                    <p style={{ fontSize: 12, color: colors.muted }}>{s.d}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Technical reference */}
            <div style={{ padding: 20, background: colors.surface2, borderRadius: 8, marginBottom: 24, fontSize: 12, color: colors.muted, lineHeight: 1.7 }}>
              <p style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10, color: colors.text }}>Technical reference</p>
              <ul style={{ paddingLeft: 18, margin: 0 }}>
                <li style={{ marginBottom: 6 }}>Verification is via a BEE-empanelled Accredited Carbon Verification Agency (ACVA); no self-certification is permitted under the Detailed Procedure for the Compliance Mechanism (BEE, July 2024).</li>
                <li style={{ marginBottom: 6 }}>CCCs are held and transacted on the ICM Registry, maintained by Grid Controller of India Limited (GRID-INDIA), separate from voluntary registries like CR-I.</li>
                <li style={{ marginBottom: 6 }}>Trading is restricted to supervised power exchanges under CERC&apos;s 2026 Terms and Conditions for Purchase and Sale of CCC Regulations; no over-the-counter trading is currently permitted.</li>
                <li style={{ marginBottom: 6 }}>CCCs can be banked indefinitely for future compliance years, but borrowing against future allocations is not permitted.</li>
                <li>GEI boundary (Scope 1 direct combustion and process emissions, Scope 2 purchased electricity and heat) is fixed at the start of the trajectory period and cannot be changed without BEE approval.</li>
              </ul>
            </div>

            {/* Footer citation */}
            <p style={{ fontSize: 11, color: colors.muted, lineHeight: 1.6 }}>
              Reference data: Greenhouse Gases Emission Intensity Target Rules, 2025, G.S.R. 739(E), 8 Oct 2025, and its Second Schedule amendment, G.S.R. 25(E), 13 Jan 2026, Ministry of Environment, Forest and Climate Change, issued under Section 14 of the Energy Conservation (Amendment) Act, 2022. Around 490 entities across seven sectors currently hold binding targets; Iron and Steel and Fertilizer are expected to bring the total closer to 740 once notified. Figures shown are the range and median across entities already notified in each sub-sector, not a legal threshold, and are illustrative only.
            </p>
          </motion.div>
        )}
      </div>
    </main>
  );
};

export default CCTSCheckerPage;
